import React, { useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, 
  MapPin, 
  ChevronRight,
  ChevronLeft,
  Plane,
  Calendar,
  CalendarClock,
  BarChart2,
  FileSignature,
  Menu,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { proposalTokens } from './tokens';

interface Destination {
  id: string;
  city: string;
  country: string;
}

interface ProposalMeta {
  eventName: string;
  clientName: string;
  dates?: string;
}

interface ProposalLayoutProps {
  children: React.ReactNode;
  activeId: string;
  onNavigate: (id: string) => void;
  destinations: Destination[];
  meta: ProposalMeta;
  /** Show the "Prelim Agenda" nav item (above Comparison) when enabled. */
  showAgenda?: boolean;
  /** Hide the Comparison nav item when the team has hidden the budget. */
  showComparison?: boolean;
}

/**
 * ProposalLayout component - Layout wrapper for proposal pages
 * Matches template4 Layout component exactly, following UI/UX guidelines
 */
export function ProposalLayout({ children, activeId, onNavigate, destinations, meta, showAgenda = false, showComparison = true }: ProposalLayoutProps) {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const lastScrollY = useRef(0);
  const mainRef = useRef<HTMLElement>(null);

  const toggleSidebar = () => setIsSidebarExpanded(!isSidebarExpanded);

  // Handle scroll to hide/show mobile header
  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target || target.scrollHeight <= target.clientHeight) return;

      const currentScrollY = target.scrollTop;
      const diff = currentScrollY - lastScrollY.current;

      if (diff > 10 && currentScrollY > 50) {
        setShowHeader(false);
      } else if (diff < -10) {
        setShowHeader(true);
      }

      lastScrollY.current = currentScrollY;
    };

    const mainElement = mainRef.current;
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      if (mainElement) {
        mainElement.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, []);

  const handleMobileNavigate = (id: string) => {
    onNavigate(id);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="flex flex-col md:flex-row h-screen w-full overflow-hidden" style={{ backgroundColor: proposalTokens.colors.cream, color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
      <style>{`
        @media (min-width: 768px) {
          .desktop-hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .desktop-hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
      
      {/* Mobile Header (Fixed) */}
      <header 
        className={cn(
          "md:hidden fixed top-0 left-0 right-0 z-50 border-b border-white/10 px-4 h-16 flex items-center justify-between transition-transform duration-300",
          showHeader ? 'translate-y-0' : '-translate-y-full'
        )}
        style={{ backgroundColor: '#000000', color: '#ffffff' }}
      >
        <div className="flex items-center">
          <img src="/images/logo_moniker_cockpit.png" alt="Moniker Cockpit" className="h-8 object-contain" />
        </div>
        
        <button 
          onClick={() => setIsMobileMenuOpen(true)}
          className="p-2 -mr-2 text-white hover:bg-white/10 rounded-md transition-transform duration-200 active:scale-90 active:rotate-90"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-[60] backdrop-blur-sm animate-fade-in"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div 
            className="absolute top-0 right-0 bottom-0 w-64 shadow-2xl flex flex-col animate-slide-in-right"
            style={{ backgroundColor: proposalTokens.colors.cream }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="h-16 flex items-center justify-between px-4 border-b" style={{ borderColor: 'rgba(0, 0, 0, 0.05)' }}>
              <span className="font-serif font-bold text-xl" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>Menu</span>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 -mr-2 rounded-md transition-transform duration-200 active:scale-90 active:-rotate-90"
                style={{ color: `${proposalTokens.colors.teal}99` }}
                onMouseEnter={(e) => e.currentTarget.style.color = proposalTokens.colors.teal}
                onMouseLeave={(e) => e.currentTarget.style.color = `${proposalTokens.colors.teal}99`}
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Main Nav */}
              <div className="space-y-1">
                <button 
                  onClick={() => handleMobileNavigate('overview')}
                  className={cn(
                    "w-full flex items-center py-3 px-3 rounded-lg transition-colors",
                    activeId === 'overview' 
                      ? 'font-medium' 
                      : 'hover:bg-black/5'
                  )}
                  style={{
                    backgroundColor: activeId === 'overview' ? `${proposalTokens.colors.teal}1a` : undefined,
                    color: activeId === 'overview' ? proposalTokens.colors.teal : `${proposalTokens.colors.teal}99`,
                  }}
                >
                  <LayoutDashboard className="w-5 h-5 mr-3" />
                  Proposal
                </button>
              </div>

              <div className="h-px" style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}></div>

              {/* Destinations */}
              {destinations.length > 0 && (
                <>
                  <div className="space-y-1">
                    <p className="px-3 text-xs font-bold uppercase tracking-wider mb-2" style={{ color: `${proposalTokens.colors.teal}66` }}>Destinations</p>
                    {destinations.map(dest => (
                      <button
                        key={dest.id}
                        onClick={() => handleMobileNavigate(dest.id)}
                        className={cn(
                          "w-full flex items-center py-3 px-3 rounded-lg transition-colors",
                          activeId === dest.id 
                            ? 'font-medium' 
                            : 'hover:bg-black/5'
                        )}
                        style={{
                          backgroundColor: activeId === dest.id ? `${proposalTokens.colors.teal}1a` : undefined,
                          color: activeId === dest.id ? proposalTokens.colors.teal : `${proposalTokens.colors.teal}99`,
                        }}
                      >
                        <MapPin className="w-5 h-5 mr-3" />
                        {dest.city}
                      </button>
                    ))}
                  </div>

                  <div className="h-px" style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}></div>
                </>
              )}

              {/* Tools */}
              <div className="space-y-1">
                {showAgenda && (
                  <button
                    onClick={() => handleMobileNavigate('agenda')}
                    className={cn(
                      "w-full flex items-center py-3 px-3 rounded-lg transition-colors",
                      activeId === 'agenda'
                        ? 'font-medium'
                        : 'hover:bg-black/5'
                    )}
                    style={{
                      backgroundColor: activeId === 'agenda' ? `${proposalTokens.colors.teal}1a` : undefined,
                      color: activeId === 'agenda' ? proposalTokens.colors.teal : `${proposalTokens.colors.teal}99`,
                    }}
                  >
                    <CalendarClock className="w-5 h-5 mr-3" />
                    Prelim Agenda
                  </button>
                )}
                {showComparison && <button
                  onClick={() => handleMobileNavigate('compare')}
                  className={cn(
                    "w-full flex items-center py-3 px-3 rounded-lg transition-colors",
                    activeId === 'compare'
                      ? 'font-medium'
                      : 'hover:bg-black/5'
                  )}
                  style={{
                    backgroundColor: activeId === 'compare' ? `${proposalTokens.colors.teal}1a` : undefined,
                    color: activeId === 'compare' ? proposalTokens.colors.teal : `${proposalTokens.colors.teal}99`,
                  }}
                >
                  <BarChart2 className="w-5 h-5 mr-3" />
                  Comparison
                </button>}
                <button 
                  onClick={() => handleMobileNavigate('sign-off')}
                  className={cn(
                    "w-full flex items-center py-3 px-3 rounded-lg transition-colors",
                    activeId === 'sign-off' 
                      ? 'font-medium' 
                      : 'hover:bg-black/5'
                  )}
                  style={{
                    backgroundColor: activeId === 'sign-off' ? `${proposalTokens.colors.teal}1a` : undefined,
                    color: activeId === 'sign-off' ? proposalTokens.colors.teal : `${proposalTokens.colors.teal}99`,
                  }}
                >
                  <FileSignature className="w-5 h-5 mr-3" />
                  Next Steps
                </button>
              </div>
            </div>

            {/* Footer Info */}
            <div className="p-4 text-white" style={{ backgroundColor: proposalTokens.colors.teal }}>
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 rounded flex items-center justify-center font-bold mr-3 text-sm" style={{ backgroundColor: proposalTokens.colors.gold, color: '#000000' }}>
                  {meta.clientName.charAt(0).toUpperCase()}
                </div>
                <div className="overflow-hidden">
                  <p className="text-sm font-bold truncate">{meta.clientName}</p>
                  <p className="text-[10px] uppercase tracking-wider truncate" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{meta.eventName}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar (Desktop) */}
      <aside 
        className={cn(
          "hidden md:flex relative z-40 h-full border-r border-black/10 flex-col transition-all duration-300 ease-in-out shadow-2xl",
          isSidebarExpanded ? 'w-72' : 'w-20'
        )}
        style={{ backgroundColor: '#000000' }}
      >
        {/* Toggle Button */}
        <button 
          onClick={toggleSidebar}
          className="absolute -right-3 top-8 rounded-full p-1 shadow-lg transition-colors z-50"
          style={{ backgroundColor: proposalTokens.colors.gold, color: '#000000' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(243, 173, 15, 0.8)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = proposalTokens.colors.gold}
        >
          {isSidebarExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Logo Area */}
        <div className={cn("p-6 border-b border-white/10 flex items-center", isSidebarExpanded ? 'justify-start' : 'justify-center')}>
          {isSidebarExpanded ? (
            <div className="flex flex-col items-start animate-fade-in">
              <img src="/images/moniker_logo_white_full.png" alt="Moniker" className="h-8 object-contain" />
            </div>
          ) : (
            <img src="/images/moniker_logo_white_full.png" alt="Moniker" className="h-8 object-contain" />
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto hide-scrollbar">
          {/* Proposal / Overview */}
          <button 
            onClick={() => onNavigate('overview')}
            className={cn(
              "w-full flex items-center py-2 px-3 rounded-lg transition-all group relative",
              activeId === 'overview' 
                ? 'bg-white/10 text-white' 
                : 'text-white/60 hover:bg-white/5 hover:text-white',
              !isSidebarExpanded && 'justify-center'
            )}
            title="Proposal"
          >
            <LayoutDashboard className={cn("w-5 h-5", isSidebarExpanded && 'mr-3', activeId === 'overview' && 'text-gold')} style={{ color: activeId === 'overview' ? proposalTokens.colors.gold : undefined }} />
            {isSidebarExpanded && <span className="font-medium">Proposal</span>}
            {!isSidebarExpanded && (
              <div className="absolute left-full ml-2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                Proposal
              </div>
            )}
          </button>

          <div className={cn("my-2 border-t border-white/10", !isSidebarExpanded && 'mx-2')}></div>

          {/* Destinations */}
          {destinations.length > 0 && (
            <div className="space-y-0.5">
              {isSidebarExpanded && <p className="px-3 pt-3 pb-2 text-xs font-bold text-white/40 uppercase tracking-wider">Destinations</p>}
              
              {destinations.map(dest => (
                <button
                  key={dest.id}
                  onClick={() => onNavigate(dest.id)}
                  className={cn(
                    "w-full flex items-center py-2 px-3 rounded-lg transition-all group relative",
                    activeId === dest.id 
                      ? 'bg-white/10 text-white' 
                      : 'text-white/60 hover:bg-white/5 hover:text-white',
                    !isSidebarExpanded && 'justify-center'
                  )}
                >
                  <MapPin className={cn("w-5 h-5", isSidebarExpanded && 'mr-3', activeId === dest.id && 'text-gold')} style={{ color: activeId === dest.id ? proposalTokens.colors.gold : undefined }} />
                  {isSidebarExpanded && (
                    <div className="text-left">
                      <span className="block font-medium leading-none">{dest.city}</span>
                      <span className="text-[10px] text-white/40 mt-0.5 block leading-normal">{dest.country}</span>
                    </div>
                  )}
                  {!isSidebarExpanded && (
                    <div className="absolute left-full ml-2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                      {dest.city}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {destinations.length > 0 && <div className={cn("my-2 border-t border-white/10", !isSidebarExpanded && 'mx-2')}></div>}

          {/* Action Tools */}
          <div className="space-y-0.5 md:pt-2">
            {showAgenda && (
              <button
                onClick={() => onNavigate('agenda')}
                className={cn(
                  "w-full flex items-center py-2 px-3 rounded-lg transition-all group relative",
                  activeId === 'agenda'
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:bg-white/5 hover:text-white',
                  !isSidebarExpanded && 'justify-center'
                )}
              >
                <CalendarClock className={cn("w-5 h-5", isSidebarExpanded && 'mr-3', activeId === 'agenda' && 'text-gold')} style={{ color: activeId === 'agenda' ? proposalTokens.colors.gold : undefined }} />
                {isSidebarExpanded && <span className="font-medium">Prelim Agenda</span>}
                {!isSidebarExpanded && (
                  <div className="absolute left-full ml-2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                    Prelim Agenda
                  </div>
                )}
              </button>
            )}
            {showComparison && <button
              onClick={() => onNavigate('compare')}
              className={cn(
                "w-full flex items-center py-2 px-3 rounded-lg transition-all group relative",
                activeId === 'compare'
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:bg-white/5 hover:text-white',
                !isSidebarExpanded && 'justify-center'
              )}
            >
              <BarChart2 className={cn("w-5 h-5", isSidebarExpanded && 'mr-3', activeId === 'compare' && 'text-gold')} style={{ color: activeId === 'compare' ? proposalTokens.colors.gold : undefined }} />
              {isSidebarExpanded && <span className="font-medium">Comparison</span>}
              {!isSidebarExpanded && (
                <div className="absolute left-full ml-2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                  Comparison
                </div>
              )}
            </button>}
            <button 
              onClick={() => onNavigate('sign-off')}
              className={cn(
                "w-full flex items-center py-2 px-3 rounded-lg transition-all group relative",
                activeId === 'sign-off' 
                  ? 'bg-white/10 text-white' 
                  : 'text-white/60 hover:bg-white/5 hover:text-white',
                !isSidebarExpanded && 'justify-center'
              )}
            >
              <FileSignature className={cn("w-5 h-5", isSidebarExpanded && 'mr-3', activeId === 'sign-off' && 'text-gold')} style={{ color: activeId === 'sign-off' ? proposalTokens.colors.gold : undefined }} />
              {isSidebarExpanded && <span className="font-medium">Next Steps</span>}
              {!isSidebarExpanded && (
                <div className="absolute left-full ml-2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                  Next Steps
                </div>
              )}
            </button>
          </div>
        </nav>

        {/* Footer / Meta Info */}
        {isSidebarExpanded && (
          <div className="p-4 border-t border-white/10" style={{ backgroundColor: '#000000' }}>
            {/* Client & Event Info */}
            <div className="mb-4">
              <div className="flex items-center">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-black font-bold text-lg mr-3" style={{ backgroundColor: proposalTokens.colors.gold }}>
                  {meta.clientName.charAt(0).toUpperCase()}
                </div>
                <div className="leading-tight">
                  <h2 className="font-bold text-white text-sm">{meta.clientName}</h2>
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">{meta.eventName}</span>
                </div>
              </div>
            </div>

            {meta.dates && (
              <div className="space-y-3">
                <div className="flex items-center text-white/60 text-sm">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center mr-3">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-white/40 font-bold">Dates</p>
                    <p className="text-xs text-white">{meta.dates}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main 
        ref={mainRef}
        className={cn(
          "flex-1 h-full overflow-hidden relative transition-all duration-300 md:pt-0",
          showHeader ? 'pt-16' : 'pt-0'
        )}
        style={{ backgroundColor: proposalTokens.colors.cream }}
      >
        {children}
      </main>
    </div>
  );
}

